import { createSlice } from '@reduxjs/toolkit';
import {
  User2FAData,
  UserForSettingsAccountModel,
  UserVirtualOfficeLicence
} from '../views/Settings/settings.types';
import {
  confirmVerification,
  settingsInit,
  confirm2FAVerification,
  updateUsername,
  changePassword,
  updateTeamSettings,
  setNotification,
  enableVerificationStatus,
  confirm2FAEmail,
  disableVerificationStatus,
  buyVirtualOffice,
  addAddress,
  getAddress,
  uploadProfileImage,
  sendEmailCodeForTwoFa,
  getBackOfficeLicences
} from '../views/Settings/Settings.actions';

export interface settingsSliceType {
  loading: boolean;
  loadingInit: boolean;
  loadingVerification: boolean;
  user: UserForSettingsAccountModel | null;
  notifications: any;
  teamSettings: number | null;
  verificationStatus: boolean;
  signIn2FAEnabled: boolean;
  signInEmailCodeVerificationEnabled: boolean;

  user2FAData: User2FAData | null;
  backupKey: string;
  licence: UserVirtualOfficeLicence | null;
  licences: UserVirtualOfficeLicence[];
}

const initialState = {
  loading: false,
  loadingInit: false,
  loadingVerification: false,
  user: null,
  notifications: null,
  teamSettings: null,
  verificationStatus: false,
  signIn2FAEnabled: false,
  signInEmailCodeVerificationEnabled: false,
  user2FAData: null,
  backupKey: '',
  licence: null,
  licences: []
} as settingsSliceType;

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    changeTeamSettings(state, { payload }) {
      state.teamSettings = payload;
    }
  },

  extraReducers: (builder) => {
    builder.addCase(settingsInit.pending, (state) => {
      state.loadingInit = true;
    });
    builder.addCase(settingsInit.fulfilled, (state, { payload }) => {
      state.loadingInit = false;
      if (payload) {
        state.user = payload.user || null;
        state.notifications = payload.notifications || null;
        state.teamSettings = payload.teamSettings || null;
        state.verificationStatus = payload.verificationStatus || false;
        state.signIn2FAEnabled = payload.signIn2FAEnabled || false;
        state.signInEmailCodeVerificationEnabled =
          payload.signInEmailCodeVerificationEnabled || false;
      }
    });
    builder.addCase(settingsInit.rejected, (state) => {
      state.loadingInit = false;
    });

    builder.addCase(confirm2FAVerification.pending, (state) => {
      state.loadingVerification = true;
    });
    builder.addCase(confirm2FAVerification.fulfilled, (state, { payload }) => {
      state.loadingVerification = false;
      state.signIn2FAEnabled = payload || !state.signIn2FAEnabled;
    });
    builder.addCase(confirm2FAVerification.rejected, (state) => {
      state.loadingVerification = false;
    });

    builder.addCase(updateUsername.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateUsername.fulfilled, (state, { payload }) => {
      state.loading = false;
      if (payload) {
        state.user = payload;
      }
    });
    builder.addCase(updateUsername.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(changePassword.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(changePassword.fulfilled, (state) => {
      state.loading = false;
    });
    builder.addCase(changePassword.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(updateTeamSettings.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateTeamSettings.fulfilled, (state, { payload }) => {
      state.loading = false;
      state.teamSettings = payload;
    });
    builder.addCase(updateTeamSettings.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(setNotification.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(setNotification.fulfilled, (state, { payload }) => {
      state.loading = false;
      const index = state.notifications?.findIndex(
        (p: any) => p.id === payload.id
      );
      state.notifications[index].checked = !state.notifications[index].checked;
    });
    builder.addCase(setNotification.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(enableVerificationStatus.pending, (state) => {
      state.loadingVerification = true;
    });
    builder.addCase(
      enableVerificationStatus.fulfilled,
      (state, { payload }) => {
        state.loadingVerification = false;
        state.user2FAData = payload;
      }
    );
    builder.addCase(enableVerificationStatus.rejected, (state) => {
      state.loadingVerification = false;
    });

    builder.addCase(confirm2FAEmail.pending, (state) => {
      state.loadingVerification = true;
    });
    builder.addCase(confirm2FAEmail.fulfilled, (state, { payload }) => {
      state.loadingVerification = false;
      if (payload !== undefined) {
        state.signInEmailCodeVerificationEnabled = payload;
      }
    });
    builder.addCase(confirm2FAEmail.rejected, (state) => {
      state.loadingVerification = false;
    });

    builder.addCase(confirmVerification.pending, (state) => {
      state.loadingVerification = true;
    });
    builder.addCase(confirmVerification.fulfilled, (state, { payload }) => {
      state.loadingVerification = false;
      if (payload) {
        state.backupKey = payload;
        state.verificationStatus = true;
      }
    });
    builder.addCase(confirmVerification.rejected, (state) => {
      state.loadingVerification = false;
    });

    builder.addCase(disableVerificationStatus.pending, (state) => {
      state.loadingVerification = true;
    });
    builder.addCase(disableVerificationStatus.fulfilled, (state) => {
      state.loadingVerification = false;
      state.signIn2FAEnabled = false;
      state.verificationStatus = false;
    });
    builder.addCase(disableVerificationStatus.rejected, (state) => {
      state.loadingVerification = false;
    });

    // VIRTUAL LICENCES
    builder.addCase(getBackOfficeLicences.pending, (state) => {
      state.loadingVerification = true;
    });
    builder.addCase(getBackOfficeLicences.fulfilled, (state, { payload }) => {
      state.loadingVerification = false;
      state.licences = payload;
    });
    builder.addCase(getBackOfficeLicences.rejected, (state) => {
      state.loadingVerification = false;
    });

    builder.addCase(buyVirtualOffice.pending, (state) => {
      state.loadingVerification = true;
    });
    builder.addCase(buyVirtualOffice.fulfilled, (state, { payload }) => {
      state.loadingVerification = false;
      // if (payload) {
      //   state.licence = payload;
      // }
    });
    builder.addCase(buyVirtualOffice.rejected, (state) => {
      state.loadingVerification = false;
    });

    builder.addCase(addAddress.pending, (state) => {
      state.loadingVerification = true;
    });
    builder.addCase(addAddress.fulfilled, (state) => {
      state.loadingVerification = false;
    });
    builder.addCase(addAddress.rejected, (state) => {
      state.loadingVerification = false;
    });

    builder.addCase(getAddress.pending, (state) => {
      state.loadingVerification = true;
    });
    builder.addCase(getAddress.fulfilled, (state) => {
      state.loadingVerification = false;
    });
    builder.addCase(getAddress.rejected, (state) => {
      state.loadingVerification = false;
    });

    builder.addCase(uploadProfileImage.pending, (state) => {
      state.loadingVerification = true;
    });
    builder.addCase(uploadProfileImage.fulfilled, (state, { payload }) => {
      state.loadingVerification = false;
      if (payload && state.user) {
        state.user.profileImageUrl = payload;
      }
    });
    builder.addCase(uploadProfileImage.rejected, (state) => {
      state.loadingVerification = false;
    });
  }
});

export const { changeTeamSettings } = settingsSlice.actions;

export default settingsSlice.reducer;
